/**
 * The shape of a field, defined ONCE for the browser and the server.
 *
 * Every entry here answers a question that was previously answered twice, or not
 * at all. The pattern is `DateField`'s: T = tomorrow and N = today live in one
 * component because "a product where nine fields honour them and the tenth does
 * not is more confusing than one where none do". A phone predicate in the browser
 * that disagrees with the one on the server is that same defect wearing a
 * different hat.
 *
 * ## Why `isPhoneShaped` exists twice, on purpose
 *
 * It was written for the vendor address import and still lives in
 * `packages/db/src/import-vendor-addresses.ts`. The browser cannot import it from
 * there — that package holds the Postgres URL and may never be reachable from a
 * browser bundle (see this package's own header, and `boundary.test.ts` which
 * enforces it by reading the sources). Neither can `packages/db` import it from
 * here: **`packages/db` does not depend on `@truckbuddy/contract`**, a boundary
 * migration `0092` records explicitly and `project-sylectus-status.ts` restates.
 *
 * So the repo's own answer to "these two must agree across a boundary we will not
 * cross" applies, and it is the `dispatchStateFromSylectusStatus` precedent: the
 * definitions sit on their own sides, and a TEST imports both and asserts they
 * agree. `phone-predicate-parity.test.ts` drives the two implementations over one
 * corpus — the import's real note cells, the real numbers, and the ReDoS inputs —
 * so a change to either side that the other does not get fails the gate.
 *
 * That is deliberately NOT the same as the copy-paste that broke
 * `v_vendor_match_queue`, where two regexes diverged silently because nothing
 * compared them. The difference is the parity test, and it is the whole reason
 * this is acceptable rather than a second source of truth.
 *
 * ## What this module deliberately does NOT do
 *
 * It does not format. A phone is stored as the user typed it, because a vendor's
 * number is their fact rather than ours, and reformatting "931-553-1305 ext 204"
 * into a canonical form throws away the extension convention the yard uses. The
 * predicate decides whether a value is a phone number at all; presentation is a
 * separate question nobody has asked.
 */
import { z } from "zod";
import { FLEET_TIMEZONE, todayInZone } from "./dates.js";

/* ────────────────────────────────────────────────────────────────────────
 * Phone
 * ──────────────────────────────────────────────────────────────────────── */

/**
 * Is this a phone number, or is it something somebody wrote instead?
 *
 * A WHITELIST, not a blacklist: a value is phone-shaped only if, after an
 * optional extension suffix, every character is a digit or phone punctuation
 * AND it carries 10 to 15 digits. Anything else — any letter, any word — is not
 * a phone number.
 *
 * The direction of the failure is chosen deliberately. A blacklist ("reject
 * values containing long words") lets an unanticipated sentence through into
 * `phone`, which is the one outcome Eden ruled out absolutely. A whitelist fails
 * the other way: an unusually formatted real phone number is called a note, and
 * it is refused in front of the person typing it, who can see what they typed.
 *
 * COPIED from `packages/db/src/import-vendor-addresses.ts` unchanged — same
 * rules, same bounds, same regexes, same ReDoS hardening. It is copied rather
 * than imported because the two packages may not import each other (see this
 * module's header), and `phone-predicate-parity.test.ts` is what stops the copy
 * from drifting: it drives both implementations over one corpus and fails if they
 * ever disagree. Change one side and the gate makes you change the other.
 */
export function isPhoneShaped(raw: string): boolean {
  const value = raw.trim();
  if (value === "") return false;

  /*
   * An extension suffix is the one alphabetic form a real phone number takes.
   *
   * ── Both whitespace quantifiers are deliberate, do not "tidy" them ────
   *
   * This read `/\s*(?:x|ext\.?|extension)\s*\d{1,6}$/i` and CodeQL flagged it as
   * `js/polynomial-redos` -- the value comes from a CSV or a text input, which is
   * uncontrolled input. Both `\s*` were ambiguous and the blowup is real, not
   * theoretical: measured on `"x".repeat(n) + " ".repeat(n)`, 2000 took 1.8 ms,
   * 8000 took 27.4 ms and 32000 took 440.6 ms -- four times the input for sixteen
   * times the work, which is the signature of a quadratic scan. The form below is
   * flat at every one of those sizes.
   *
   * The LEADING `\s*` is simply gone. It was never needed: what follows tests the
   * remainder against `/^[0-9 ()+\-./]+$/`, whose class already contains a space,
   * so leaving the separator behind changes no answer.
   *
   * The inner one is BOUNDED. An extension marker is followed by a space or two,
   * never by four.
   *
   * `packages/monitoring-db/src/guards.ts` is the precedent for taking this
   * seriously -- two ReDoS findings were fixed there, and its comment says the
   * same thing: a regex that looks tidier is how the vulnerability comes back.
   */
  const withoutExtension = value.replace(/(?:x|ext\.?|extension)\s{0,4}\d{1,6}$/i, "");
  if (!/^[0-9 ()+\-./]+$/.test(withoutExtension)) return false;

  const digits = value.replace(/\D/g, "");
  return digits.length >= 10 && digits.length <= 15;
}

/**
 * The stored value if it is a phone number, or `null` if it is anything else.
 *
 * Eden's ruling of 15 September 2026, as a function: *"if there are notes in the
 * phone number column skip that instance. it is clear that they do not have a
 * phone number on record at the moment."* A note in `phone` is not a bad phone
 * number, it is the ABSENCE of one — so every reader treats it exactly as it
 * treats a `null`, and no screen has to know that the column is free text with
 * about 25 rows of prose in it.
 *
 * It does NOT delete or rewrite anything. The note stays in the column, because
 * `import:vendor-addresses` refuses to overwrite it on the grounds that no
 * automatic process should decide whether a real phone has been painted over, and
 * a display helper has even less standing to decide that than an import does.
 */
export function usablePhone(stored: string | null | undefined): string | null {
  if (stored === null || stored === undefined) return null;
  return isPhoneShaped(stored) ? stored : null;
}

/**
 * The longest phone number worth storing.
 *
 * Fifteen digits is E.164's ceiling and `isPhoneShaped` already enforces it; this
 * bounds the PUNCTUATION and the extension around them, so a value cannot be
 * padded to the old 500-character limit with brackets and still pass. Forty is
 * comfortably above the longest real form ("+1 (931) 553-1305 extension 204" is
 * 31).
 */
export const PHONE_MAX = 40;

/** What a refused phone is told, in the words the user reads. */
export const PHONE_REFUSAL =
  "That does not look like a phone number. Use digits, with 10 to 15 of them — " +
  "brackets, spaces, +, - . / and a trailing extension are fine. " +
  "Anything you want to say in words belongs in Notes.";

/**
 * A phone number, refused outright when it is not one (Eden, 15 September 2026).
 *
 * `.nullish()` because most vendors have no phone on record and that is a real
 * state, not a missing one. An empty string normalises to `null` so "no phone" is
 * one value in the column rather than two that sort differently — the same rule
 * the vendor address route already applies to every text column.
 */
export const phoneString = z
  .string()
  .trim()
  .max(PHONE_MAX, PHONE_REFUSAL)
  .refine((value) => value === "" || isPhoneShaped(value), PHONE_REFUSAL)
  .transform((value) => (value === "" ? null : value))
  .nullish();

/* ────────────────────────────────────────────────────────────────────────
 * Dates
 * ──────────────────────────────────────────────────────────────────────── */

/**
 * How far back any date field will accept, in years (Eden, 15 September 2026).
 *
 * This is a TYPO NET, not a business rule. Backdating by days or weeks is
 * ordinary — a work order raised last week, an invoice that took a fortnight to
 * arrive — and refusing it would break real data entry. What it catches is the
 * slipped keystroke that turns 2026 into 1983, which is what Eden actually hit.
 *
 * Five years rather than one because the MaintainX history import and any
 * catch-up entry can legitimately reach back further than a year, and a floor
 * that refuses real data is a worse failure than one that admits an implausible
 * date nobody typed by accident.
 */
export const DATE_FLOOR_YEARS = 5;

/**
 * What a date field is FOR, which is what decides its window.
 *
 * - `scheduled` — a date being planned toward: a due date, a needed-by. The
 *   future is the whole point, so there is no ceiling; only the typo floor.
 * - `recorded` — a date something already happened on: a work date, a payment,
 *   a down-since. The future is impossible, so today is the ceiling.
 *
 * The two are separated because one window cannot serve both, and the failure of
 * pretending otherwise is silent: a `recorded` field with no ceiling accepts work
 * logged for next March, which `post_time_entry` then refuses with a 422 the
 * dialog never predicted.
 */
export type DateFieldPurpose = "scheduled" | "recorded";

export interface DateWindow {
  /** Inclusive floor, `YYYY-MM-DD`. */
  min: string;
  /** Inclusive ceiling, `YYYY-MM-DD`, or `null` where the future is allowed. */
  max: string | null;
}

/**
 * The window a field of this purpose accepts, as of `now`.
 *
 * `now` is a parameter rather than a call to `new Date()` inside, because a
 * window that cannot be tested at a fixed instant cannot be tested at all — and
 * because the fleet timezone, not the browser's, is what decides which day it is
 * here. Every clock in this product already reads the fleet zone; a floor
 * computed in the user's zone would move under a night-shift user in Nevada.
 *
 * The floor keeps the month and day and moves only the year, so it lands on a
 * real date without constructing one: 29 February minus five years would be
 * 2021-02-29, which does not exist, and the string comparison below treats it as
 * the instant before 1 March 2021 rather than throwing. That is the correct
 * behaviour for a floor and costs nothing; building a `Date` to "fix" it would
 * reintroduce the roll-forward trap that bit the zone formatters.
 */
export function dateWindow(
  purpose: DateFieldPurpose,
  now: Date,
  timeZone: string = FLEET_TIMEZONE,
): DateWindow {
  const today = todayInZone(now, timeZone);
  const [year, month, day] = today.split("-");
  const min = `${String(Number(year) - DATE_FLOOR_YEARS).padStart(4, "0")}-${month}-${day}`;
  return { min, max: purpose === "recorded" ? today : null };
}

/** The bare shape, with no range attached. Kept separate so the two can be tested apart. */
export const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Does this well-formed date name a day that actually exists?
 *
 * `DATE_ONLY_RE` admits `2026-02-30`, and CLAUDE.md records why that matters:
 * **V8 does not reject an impossible day, it rolls it forward** — `new Date(
 * "2026-02-30")` is 2 March. So a string that passes the shape check and is
 * inside the window would reach Postgres, which is stricter than JavaScript and
 * answers `date/time field value out of range`. That surfaces as a 500 or a raw
 * SQLSTATE rather than as a sentence naming the problem.
 *
 * The check is a ROUND TRIP, not a parse of the string: the parts are pulled out
 * by hand and fed to `Date.UTC`, then read back. If the day rolled, the parts
 * that come back differ from the ones that went in. This never asks V8 to
 * interpret the user's text, which is the trap it exists to avoid.
 *
 * UTC throughout, because this asks only whether a day exists on the calendar —
 * a question with no timezone in it. Using the local constructor would make the
 * answer depend on the machine.
 */
export function isRealCalendarDay(value: string): boolean {
  const year = Number(value.slice(0, 4));
  const month = Number(value.slice(5, 7));
  const day = Number(value.slice(8, 10));
  const probe = new Date(Date.UTC(year, month - 1, day));
  return (
    probe.getUTCFullYear() === year &&
    probe.getUTCMonth() === month - 1 &&
    probe.getUTCDate() === day
  );
}

/**
 * Why a date was refused, or `null` when it was not.
 *
 * Returns the sentence rather than a boolean so the browser and the server say
 * the same thing, and so the message can name the limit — "the earliest you can
 * pick is 2021-09-15" is actionable where "invalid date" is not.
 */
export function dateOutOfWindow(
  value: string,
  purpose: DateFieldPurpose,
  now: Date,
  timeZone: string = FLEET_TIMEZONE,
): string | null {
  if (!DATE_ONLY_RE.test(value)) return "Enter a date as YYYY-MM-DD.";
  if (!isRealCalendarDay(value)) return `There is no such day as ${value}.`;
  const { min, max } = dateWindow(purpose, now, timeZone);
  /*
   * String comparison, and that is not a shortcut — it is what keeps this free of
   * the trap the zone formatters hit. Zero-padded ISO dates sort lexically in
   * calendar order, so no parsing is needed; and nothing here constructs a Date
   * from `value`, so "2026-02-30" is refused by the regex or compared as text
   * rather than being silently rolled to 2 March by V8.
   */
  if (value < min) {
    return `That date is too far in the past — the earliest you can pick is ${min}.`;
  }
  if (max !== null && value > max) {
    return `That date is in the future — the latest you can pick is ${max}.`;
  }
  return null;
}

/**
 * A `YYYY-MM-DD` inside its purpose's window.
 *
 * The clock is read at PARSE time, not at module load: a schema that captured
 * "today" when the process started would drift on any server that stays up
 * overnight, and every collector container does.
 */
export function boundedDateString(purpose: DateFieldPurpose) {
  return z.string().superRefine((value, ctx) => {
    const problem = dateOutOfWindow(value, purpose, new Date());
    if (problem !== null) ctx.addIssue({ code: "custom", message: problem });
  });
}

/* ────────────────────────────────────────────────────────────────────────
 * Numbers
 * ──────────────────────────────────────────────────────────────────────── */

/**
 * Drop everything that is not part of a decimal number, keeping what the user has
 * typed SO FAR intact.
 *
 * This is the whole of the "you cannot type text into a cost field" fix, and the
 * hard part is not the filtering — it is that a half-typed number must survive.
 * "1." is not a valid decimal and must not be normalised to "1", or the user can
 * never reach "1.5": the keystroke that opens the decimal would be undone as fast
 * as it is typed. Same for a lone "-" and a leading ".".
 *
 * Applied to the INPUT event rather than to keypresses, which is what keeps paste,
 * IME composition, autofill, dictation and screen readers working. Swallowing
 * keystrokes blocks the letter and also blocks the assistive technology that has
 * to deliver text some other way; filtering the resulting value blocks only the
 * letter.
 *
 * `scale` is the number of decimal places this kind of value carries. The three
 * that exist are documented in `money.ts` and are load-bearing: amounts 2, rates
 * and unit costs 4, quantities 3. Passing the wrong one here silently truncates
 * a rate to cents, so callers take it from the same constant the parser does.
 */
export function sanitiseDecimal(
  raw: string,
  scale: number,
  options?: { allowNegative?: boolean },
): string {
  const allowNegative = options?.allowNegative ?? false;
  let out = "";
  let seenPoint = false;
  let decimals = 0;

  for (const character of raw) {
    if (character === "-") {
      // Only ever leading, and only where the field admits one. A minus in the
      // middle of a number is a typo, not a sign.
      if (allowNegative && out === "") out = "-";
      continue;
    }
    if (character === ".") {
      // A second point is dropped rather than replacing the first, so "1.2.3"
      // reads as "1.23" — the digits the user typed, in the order they typed
      // them, which is what they would have got had the stray key not fired.
      if (!seenPoint && scale > 0) {
        seenPoint = true;
        out += ".";
      }
      continue;
    }
    if (character >= "0" && character <= "9") {
      if (seenPoint) {
        if (decimals >= scale) continue;
        decimals += 1;
      }
      out += character;
      continue;
    }
    // Everything else — letters, emoji, currency symbols, whitespace — is gone.
  }

  return out;
}

/** A whole number. Points are dropped rather than rounded; there is nothing to round. */
export function sanitiseInteger(raw: string, options?: { allowNegative?: boolean }): string {
  return sanitiseDecimal(raw, 0, options);
}

/* ────────────────────────────────────────────────────────────────────────
 * Free text
 * ──────────────────────────────────────────────────────────────────────── */

/**
 * The length bounds the browser applies, named rather than spelled out at each
 * call site so a field and its schema cannot drift apart.
 *
 * These are the bounds `requests.ts` already declares. Nothing here is a new
 * policy — the server has enforced all of it since those schemas were written.
 * What is new is that the input now knows, so a user who reaches the limit is
 * told by the control instead of by a 400 after they press Save.
 */
export const TEXT_MAX = {
  /** A name, a label, a title. */
  name: 200,
  /** A short code or reference — a PO number, an invoice number. */
  code: 100,
  /** One line of prose: a summary, an address line, a service area. */
  line: 500,
  /** A paragraph: notes, a description, a reason, a cause. */
  prose: 1000,
  /** An email address. RFC 5321's practical ceiling. */
  email: 320,
  /** A URL. */
  url: 500,
} as const;

export type TextKind = keyof typeof TEXT_MAX;

/* ────────────────────────────────────────────────────────────────────────
 * Database integer bounds
 * ──────────────────────────────────────────────────────────────────────── */

/**
 * The largest value a Postgres `integer` (int4) holds.
 *
 * A request number with no upper bound that lands in an `integer` column or
 * parameter fails inside Postgres as 22003, which nothing maps to a sentence, so
 * a malformed request answered 500. Bound the number here instead.
 */
export const INT4_MAX = 2_147_483_647;

/** The largest value a Postgres `bigint` (int8) holds. */
export const INT8_MAX = 9_223_372_036_854_775_807n;

/** True when `value` is decimal digits naming a number a `bigint` can hold. */
export function isBigintId(value: string): boolean {
  return /^\d+$/.test(value) && BigInt(value) <= INT8_MAX;
}

/**
 * A bigint id carried as text, which is how every bigint crosses the wire
 * (TRBUD-185). Digits alone were not a bound: a 20-digit id passed the old
 * `/^\d+$/` and failed in Postgres as 22003, answering 500 where a non-numeric
 * id already answered 400.
 */
export function bigintIdString(message: string) {
  return z.string().refine(isBigintId, { message });
}
